/**
 * Pure routing decision for the URL shapes this app serves. Kept free of DOM
 * globals (`window.location`, etc.) so it's unit-testable with plain
 * `node --test`; `main.ts` passes in `location.pathname` and the `authToken`
 * query param it already parsed.
 *
 * - Host: `/room?authToken=<token>` — the `authToken` query param always
 *   wins, regardless of path.
 * - Personalized guest: `/<meetingId>/<inviteeId>` (two path segments), per
 *   backend-feathub#162. The invitee is already identified server-side, so
 *   no name-capture step is needed (meet-personalized-skip-name, issue #7).
 * - Anonymous guest: `/<token>` (single path segment), the classic magic
 *   link redeemed via backend-feathub#161.
 *
 * The host flow makes no backend calls of its own (see `host.ts`), so its
 * `callType` (issue #8) can only reach this app the same way `authToken`
 * does: as a `callType` query param the hub app embeds in the host link.
 */
import { parseCallType, type CallType } from "./call-type.ts";

export type Route =
  | { kind: "host"; authToken: string; callType: CallType }
  | { kind: "anonymous-guest"; token: string }
  | { kind: "personalized-guest"; meetingId: string; inviteeId: string }
  | { kind: "invalid" };

export function resolveRoute(
  pathname: string,
  authToken: string | null,
  callType: string | null,
): Route {
  if (authToken) {
    return { kind: "host", authToken, callType: parseCallType(callType) };
  }

  const segments = pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);

  if (segments.length === 2) {
    const [meetingId, inviteeId] = segments;
    return { kind: "personalized-guest", meetingId, inviteeId };
  }

  if (segments.length === 1 && segments[0] !== "room") {
    return { kind: "anonymous-guest", token: segments[0] };
  }

  return { kind: "invalid" };
}
